import React, { useMemo, useState, useEffect } from 'react';
import { Award, BookOpenCheck, History } from 'lucide-react';
import { apiClient } from '../../services/authService';
import WelcomeAndFilters from './WelcomeAndFilters';
import StatCard from './StatCard';
import ExamLearningCard from './ExamLearningCard';
import EmptySearchPirucat from './EmptySearchPirucat';
import { flattenExamsWithMeta, filterExams } from './examDashboardUtils';
import { readLearningStats, getExamAttempt } from './learningStatsStorage';
import './availableExamsDashboard.css';

function formatLastActivity(iso) {
  if (!iso) return 'No recent activity';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return 'No recent activity';
  }
}

/**
 * Learning dashboard (Available Exams).
 * @param {{ grupos: Array<{ empresa?: object, examenes?: object[] }>, onStartExam: (id: number|string) => void }} props
 */
function AvailableExamsDashboard({ grupos, onStartExam }) {
  const [username, setUsername] = useState(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [difficulty, setDifficulty] = useState('All');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get('/auth/me');
        if (!cancelled && data?.username) setUsername(data.username);
      } catch {
        if (!cancelled) setUsername(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const flatAll = useMemo(() => flattenExamsWithMeta(grupos), [grupos]);

  const filtered = useMemo(
    () => filterExams(flatAll, { search, category, difficulty }),
    [flatAll, search, category, difficulty]
  );

  const quickStats = useMemo(() => {
    const stats = readLearningStats();
    const ids = new Set(flatAll.map((e) => String(e.id)));
    const attempts = stats.attempts || {};
    let completed = 0;
    let sum = 0;
    let countScores = 0;
    let lastIso = null;

    for (const [id, row] of Object.entries(attempts)) {
      if (!ids.has(id)) continue;
      completed += 1;
      if (typeof row.scorePercent === 'number') {
        sum += row.scorePercent;
        countScores += 1;
      }
      if (row.at && (!lastIso || row.at > lastIso)) lastIso = row.at;
    }

    const total = flatAll.length;
    const avg = countScores > 0 ? Math.round(sum / countScores) : null;
    const activityValue = lastIso ? formatLastActivity(lastIso) : '—';
    const activityHint = lastIso
      ? 'Based on attempts saved on this device'
      : 'Finish an exam to see your last activity here';

    return {
      completedLabel: `${completed}/${total || 0}`,
      avgLabel: avg != null ? `${avg}%` : '—',
      activityValue,
      activityHint,
    };
  }, [flatAll]);

  const welcomeTitle = username ? `Hello, ${username}!` : 'Hello!';
  const welcomeSubtitle = 'What would you like to study today?';

  const clearFilters = () => {
    setSearch('');
    setCategory('All');
    setDifficulty('All');
  };

  if (flatAll.length === 0) {
    return (
      <div className="exam-dashboard">
        <div className="exam-dashboard__inner">
          <div className="exam-dashboard__empty-api">
            <p>No exams available</p>
            <p>When your instructor or administrator publishes exams, they will appear here.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="exam-dashboard">
      <div className="exam-dashboard__inner">
        <WelcomeAndFilters
          welcomeTitle={welcomeTitle}
          welcomeSubtitle={welcomeSubtitle}
          search={search}
          onSearchChange={setSearch}
          category={category}
          onCategoryChange={setCategory}
          difficulty={difficulty}
          onDifficultyChange={setDifficulty}
        />

        <section aria-label="Quick summary" className="exam-dashboard__stats">
          <StatCard
            icon={<BookOpenCheck width={20} height={20} aria-hidden />}
            label="Exams completed"
            value={quickStats.completedLabel}
            hint="Based on attempts recorded on this device"
          />
          <StatCard
            icon={<Award width={20} height={20} aria-hidden />}
            label="Average score"
            value={quickStats.avgLabel}
            hint={quickStats.avgLabel === '—' ? 'Complete an exam to see your average' : 'Across attempts with a saved score'}
          />
          <StatCard
            icon={<History width={20} height={20} aria-hidden />}
            label="Last activity"
            value={quickStats.activityValue}
            hint={quickStats.activityHint}
          />
        </section>

        {filtered.length === 0 ? (
          <EmptySearchPirucat onClearFilters={clearFilters} />
        ) : (
          <section aria-label="Available exams list">
            <h2 className="exam-dashboard__section-title">Available exams</h2>
            <div className="exam-dashboard__exam-grid">
              {filtered.map((exam) => (
                <ExamLearningCard
                  key={exam.id}
                  exam={exam}
                  attempt={getExamAttempt(exam.id)}
                  onStartExam={onStartExam}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default AvailableExamsDashboard;
